import { CalendarIcon } from 'lucide-react';
import * as React from 'react';
import type { DateRange } from 'react-day-picker';

import { DimeButton } from './DimeButton.js';
import { DimeCalendar } from './DimeCalendar.js';
import { DimeFilterButton } from './DimeFilterButton.js';
import { DimePopover, DimePopoverContent, DimePopoverTrigger } from './DimePopover.js';
import { cn } from './lib/utils.js';
import { formatLocalDate, validDateOrUndefined } from './lib/ymd-date.js';

/** The picker's value, re-exported so app code never has to reach for react-day-picker itself. */
export type DimeDateRange = DateRange;

/**
 * A named range the reader can take in one click.
 *
 * `range` is a value rather than a factory: the caller recomputes it on render anyway, and a
 * factory would let a preset resolve to a different window than the label the reader just read.
 */
export interface DimeDateRangePreset {
  label: string;
  range: DateRange;
}

export interface DimeDateRangePickerProps {
  value?: DateRange;
  defaultValue?: DateRange;
  onValueChange?: (range: DateRange | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  /** Shown as a rail beside the months. Omit for a calendar with no shortcuts. */
  presets?: readonly DimeDateRangePreset[];
  /** Names a button that empties a set range; omit it and a picked range can only be replaced. */
  clearLabel?: string;
  calendarProps?: Omit<React.ComponentProps<typeof DimeCalendar>, 'mode' | 'selected' | 'onSelect'>;
  /** `filter` renders the trigger as a small outline filter button, to sit in a table toolbar beside the other filters. */
  appearance?: 'field' | 'filter';
}

export function DimeDateRangePicker(props: Readonly<DimeDateRangePickerProps>) {
  const {
    value,
    defaultValue,
    onValueChange,
    placeholder = 'Pick a date range',
    disabled,
    className,
    id,
    'aria-invalid': ariaInvalid,
    'aria-describedby': ariaDescribedBy,
    presets,
    clearLabel,
    calendarProps,
    appearance = 'field',
  } = props;
  // Whether the caller passed `value` at all -- not whether it is currently set. A controlled
  // caller whose `value` starts undefined and later clears back to undefined must still read as
  // controlled, or `value ?? internal` keeps showing the range the caller just cleared.
  const isControlled = 'value' in props;
  const [open, setOpen] = React.useState(false);
  // WHY: react-day-picker reads the first click as a complete one-day range, so which click this is has to be counted here.
  const [awaitingEnd, setAwaitingEnd] = React.useState(false);
  const [internal, setInternal] = React.useState<DateRange | undefined>(defaultValue);
  const selected = isControlled ? value : internal;

  function commit(range: DateRange | undefined) {
    if (!isControlled) setInternal(range);
    onValueChange?.(range);
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (next) setAwaitingEnd(false);
  }

  /** The first click after opening starts a fresh range on that day; the second, on any day including that one, ends it and closes. */
  function handleSelect(range: DateRange | undefined, clickedDay: Date) {
    if (!awaitingEnd) {
      commit({ from: clickedDay, to: clickedDay });
      setAwaitingEnd(true);
      return;
    }
    commit(range);
    setAwaitingEnd(false);
    setOpen(false);
  }

  function handleClear() {
    commit(undefined);
    setAwaitingEnd(false);
    setOpen(false);
  }

  // An Invalid Date reads as no date at that end, never a throw, and a range with no readable start
  // is no range: the label, the muted text, Clear and the calendar all read this one range.
  const from = validDateOrUndefined(selected?.from);
  const to = validDateOrUndefined(selected?.to);
  const shown: DateRange | undefined = from ? { from, to } : undefined;
  const hasRange = shown !== undefined;
  const label = from
    ? to
      ? `${formatLocalDate(from)} – ${formatLocalDate(to)}`
      : formatLocalDate(from)
    : placeholder;

  return (
    <DimePopover open={open} onOpenChange={handleOpenChange}>
      <DimePopoverTrigger asChild>
        {appearance === 'filter' ? (
          <DimeFilterButton
            id={id}
            label={label}
            icon={<CalendarIcon className="size-3.5" aria-hidden />}
            hideLabelBelowSm={false}
            disabled={disabled}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            className={cn('font-normal', !from && 'text-muted-foreground', className)}
          />
        ) : (
          <DimeButton
            id={id}
            variant="outline"
            disabled={disabled}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            className={cn(
              'w-72 justify-start text-left font-normal',
              !from && 'text-muted-foreground',
              className,
            )}
          >
            <CalendarIcon className="mr-2 size-4" />
            {label}
          </DimeButton>
        )}
      </DimePopoverTrigger>
      <DimePopoverContent className="w-auto p-0" align="start">
        <div className="flex flex-col sm:flex-row">
          {presets?.length ? (
            // A list, not a row of buttons: these are named jumps through the same calendar, and
            // a reader on a phone gets them stacked above the months rather than squeezed beside.
            <ul className="flex flex-row gap-1 overflow-x-auto border-b p-2 sm:w-40 sm:flex-col sm:border-r sm:border-b-0">
              {presets.map((preset) => (
                <li key={preset.label}>
                  <DimeButton
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start font-normal whitespace-nowrap"
                    onClick={() => {
                      commit(preset.range);
                      setOpen(false);
                    }}
                  >
                    {preset.label}
                  </DimeButton>
                </li>
              ))}
            </ul>
          ) : null}
          <DimeCalendar
            mode="range"
            // A second click on a one-day range's own day confirms it, rather than clearing it.
            required
            selected={shown}
            onSelect={handleSelect}
            numberOfMonths={2}
            {...calendarProps}
          />
        </div>
        {clearLabel && hasRange ? (
          <div className="flex justify-end border-t p-2">
            <DimeButton
              type="button"
              variant="ghost"
              size="sm"
              className="font-normal"
              onClick={handleClear}
            >
              {clearLabel}
            </DimeButton>
          </div>
        ) : null}
      </DimePopoverContent>
    </DimePopover>
  );
}
